import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import Layout from "@components/Layout";
import Text from "@components/Text";
import Select, { TOption } from "@components/Select";
import Input from "@components/Input";
import SizedBox from "@components/SizedBox";
import Button from "@components/Button";
import Loading from "@components/Loading";
import { Column } from "@components/Flex";
import { useLang } from "@src/hooks/useLang";
import { useStores } from "@stores";
import { LANGUAGE, LANGUAGE_NAMES, } from "@stores/SettingsStore";
import { WeightUnit, AreaUnit } from "@services/demosService";
import { BUSHEL_CROPS, bushelWeightForCrop } from "@src/utils/convert";
import { toast } from "react-toastify";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import JohnDeereProfileSection from "@screens/ProfileScreen/JohnDeereProfileSection";
import CropwiseProfileSection from "@screens/ProfileScreen/CropwiseProfileSection";
import DeviceAlertsSection from "./DeviceAlertsSection";
import ConnectedAppsSection from "./ConnectedAppsSection";

const SettingsGrid = styled.div`
  width: 100%;
  display: grid;
  column-gap: 16px;
  row-gap: 16px;
  grid-template-columns: 1fr 1fr 1fr;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr;
  }
`;

/**
 * Integration cards, laid out three-up on desktop like SettingsGrid above —
 * these panels are narrow and there's a whole row of unused width otherwise.
 * Two columns on tablet, stacked on mobile. Cards stretch to the tallest in the
 * row so their bottoms line up.
 */
const IntegrationsGrid = styled.div`
  width: 100%;
  display: grid;
  column-gap: 16px;
  row-gap: 16px;
  grid-template-columns: 1fr 1fr 1fr;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr;
  }
`;

// Same columns as IntegrationsGrid, so the card lines up with the ones above;
// collapses when ConnectedAppsSection renders nothing.
const ConnectedAppsGrid = styled(IntegrationsGrid)`
  margin-top: 16px;

  &:empty {
    display: none;
  }
`;

/**
 * Fills Layout's scrolling Content (at least its height, more when the form is
 * long) so the footer can sit at the bottom of a short page. On desktop it also
 * runs into Content's 32px bottom padding, so the sticky footer is flush with
 * the window edge both while scrolling and at the end of the scroll.
 */
const Page = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;

  @media (min-width: ${MOBILE_BREAKPOINT + 1}px) {
    margin-bottom: -32px;
  }
`;

/**
 * Save / Cancel. Pinned to the bottom of the window on desktop: with the
 * integration cards the page is taller than the screen, and the buttons used
 * to be the last thing in the scroll. Only the buttons float: the bar itself is
 * transparent and lets clicks through, so it doesn't hide the cards behind it.
 */
const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: auto;

  @media (min-width: ${MOBILE_BREAKPOINT + 1}px) {
    position: sticky;
    bottom: 0;
    z-index: 1;
    padding: 16px 0;
    pointer-events: none;

    & > button {
      pointer-events: auto;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    }
  }
`;

const SettingRow = styled.div`
  display: flex;
  flex-direction: column;
`;

const SettingLabel = styled(Text)`
  margin-bottom: 4px;
`;

const SettingDescription = styled(Text)`
  margin-bottom: 8px;
`;

interface SettingsFormData {
  language: LANGUAGE;
  weightUnit: WeightUnit;
  areaUnit: AreaUnit;
  poundsPerBushel: number;
  poundsPerBag: number;
  bushelCropId: string;
}

const SettingsScreen: React.FC = () => {

  const { settingsStore, accountStore } = useStores();
  const lang = useLang();
  const [loading, setLoading] = useState(false);

  const fromStore = (): SettingsFormData => ({
    language: settingsStore.language,
    weightUnit: settingsStore.weightUnit,
    areaUnit: settingsStore.areaUnit,
    poundsPerBushel: settingsStore.poundsPerBushel,
    poundsPerBag: settingsStore.poundsPerBag,
    bushelCropId: settingsStore.bushelCropId,
  });

  const [formData, setFormData] = useState<SettingsFormData>(fromStore);

  // Whether the user has edited anything since the form was last seeded from
  // the store. A ref, not state: it must not trigger a render, only gate one.
  const dirty = useRef(false);
  const updateForm = (next: SettingsFormData) => {
    dirty.current = true;
    setFormData(next);
  };

  // The stored preferences arrive a moment after this page mounts on a reload
  // (they load after the session check), and until now the form kept the
  // defaults it was seeded with — so someone who switched to acres, saved and
  // reloaded saw hectares again and had to do it twice. Re-seed once they land,
  // unless the user has already started editing.
  useEffect(() => {
    if (settingsStore.prefsLoaded && !dirty.current) setFormData(fromStore());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsStore.prefsLoaded]);

  const weightUnits: TOption[] = [
    { value: WeightUnit.Ton, title: lang.settings.ton },
    { value: WeightUnit.Centner, title: lang.settings.centner },
    { value: WeightUnit.Kilogram, title: lang.settings.kilograms },
    { value: WeightUnit.Pound, title: lang.settings.pounds },
    { value: WeightUnit.ShortTon, title: lang.settings.shortTon },
    { value: WeightUnit.Bushel, title: lang.settings.bushels },
    { value: WeightUnit.Bag, title: lang.settings.bag },
  ];

  const areaUnits = [
    { value: AreaUnit.Hectare, title: lang.settings.hectares },
    { value: AreaUnit.Acre, title: lang.settings.acres },
  ];

  // Bushel weight is a property of the crop, not of the farm, so the picker
  // fills the number in rather than leaving the user to recall that corn is 56
  // and wheat is 60. "Custom" stays selectable: bagged or contract crops that
  // have no statutory bushel still need a hand-typed weight.
  const bushelCropOptions: TOption[] = [
    ...BUSHEL_CROPS.map((crop) => ({
      value: crop.cropId,
      title: `${(lang.crops as Record<string, string>)[crop.langKey] ?? crop.cropId} — ${crop.poundsPerBushel} lb`,
    })),
    { value: "", title: lang.settings.bushelCropCustom },
  ];

  const languageOptions: TOption[] = Object
    .entries(LANGUAGE_NAMES)
    .map(([code, name]) => ({
      value: code,
      title: name,
    }));

  const handleSave = async () => {
    setLoading(true);
    try {
      // One save, not five: each setter used to write (and now PUT) on its own.
      settingsStore.updateSettings(formData);
      dirty.current = false;
      toast(lang.settings.saveSuccess, { type: "success" });
    } catch (error: any) {
      toast(error.message ?? error.toString(), { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    dirty.current = false;
    setFormData(fromStore());
  };

  return (
    <Layout
      title={lang.settings.screenTitle}
      subtitle={lang.settings.screenSubtitle}
    >
      <Page>
        <Column crossAxisSize="max">
          <Text
            type="heading"
            size={11}
            color="#899093"
            style={{ textTransform: "uppercase" }}
          >{lang.settings.generalHeading}</Text>
          <Text size={14} color="#6B7280" style={{ marginBottom: 16 }}>
            {lang.settings.generalDescription}
          </Text>
          <SettingsGrid>
            <SettingRow>
              <SettingLabel size={14}>{lang.settings.languages}</SettingLabel>
              <SettingDescription size={12} color="#899093">{lang.settings.languagesDescription}</SettingDescription>
              <Select
                value={formData.language}
                onChange={(value) => updateForm({
                  ...formData,
                  language: value as LANGUAGE
                })}
                options={languageOptions}
              />
            </SettingRow>
          </SettingsGrid>

          <SizedBox height={32} />

          <Text
            type="heading"
            size={11}
            color="#899093"
            style={{ textTransform: "uppercase" }}
          >{lang.settings.measurementUnits}</Text>
          <Text size={14} color="#6B7280" style={{ marginBottom: 16 }}>{lang.settings.measurementUnitsDescription}</Text>
          <SettingsGrid>
            <SettingRow>
              <SettingLabel size={14}>{lang.settings.areaUnit}</SettingLabel>
              <SettingDescription size={12} color="#899093">{lang.settings.areaUnitDescription}</SettingDescription>
              <Select
                value={formData.areaUnit}
                onChange={(value) => updateForm({
                  ...formData,
                  areaUnit: value as AreaUnit,
                })}
                options={areaUnits}
              />
            </SettingRow>

            <SettingRow>
              <SettingLabel size={14}>{lang.settings.weightUnit}</SettingLabel>
              <SettingDescription size={12} color="#899093">{lang.settings.weightUnitDescription}</SettingDescription>
              <Select
                value={formData.weightUnit}
                onChange={(value) => updateForm({
                  ...formData,
                  weightUnit: value as WeightUnit
                })}
                options={weightUnits}
              />
              {formData.weightUnit === WeightUnit.Bushel && (
                <>
                  <SizedBox height={16} />
                  <SettingLabel size={14}>{lang.settings.bushelCrop}</SettingLabel>
                  <SettingDescription size={12} color="#899093">{lang.settings.bushelCropDescription}</SettingDescription>
                  <Select
                    value={formData.bushelCropId}
                    onChange={(value) => updateForm({
                      ...formData,
                      bushelCropId: value,
                      // Picking a crop is the whole point of the control, so it
                      // overwrites the weight. Picking "Custom" leaves whatever
                      // is in the box for the user to edit.
                      poundsPerBushel: bushelWeightForCrop(value) ?? formData.poundsPerBushel,
                    })}
                    options={bushelCropOptions}
                  />
                  <SizedBox height={16} />
                  <SettingLabel size={14}>{lang.settings.poundsPerBushel}</SettingLabel>
                  <SettingDescription size={12} color="#899093">{lang.settings.poundsPerBushelDescription}</SettingDescription>
                  <Input
                    type="number"
                    value={formData.poundsPerBushel.toString()}
                    onChange={(e) => {
                      const poundsPerBushel = parseFloat(e.target.value);
                      updateForm({
                        ...formData,
                        poundsPerBushel,
                        // Typing a weight that is no longer the selected crop's
                        // standard drops the label to Custom, so the form never
                        // claims "Corn" next to a number that is not 56.
                        bushelCropId:
                          bushelWeightForCrop(formData.bushelCropId) === poundsPerBushel
                            ? formData.bushelCropId
                            : "",
                      });
                    }}
                    placeholder="60"
                    min="1"
                    step="1"
                  />
                </>
              )}
              {formData.weightUnit === WeightUnit.Bag && (
                <>
                  <SizedBox height={16} />
                  <SettingLabel size={14}>{lang.settings.poundsPerBag}</SettingLabel>
                  <SettingDescription size={12} color="#899093">{lang.settings.poundsPerBagDescription}</SettingDescription>
                  <Input
                    type="number"
                    value={formData.poundsPerBag.toString()}
                    onChange={(e) => updateForm({
                      ...formData,
                      poundsPerBag: parseFloat(e.target.value)
                    })}
                    placeholder="100"
                    min="1"
                    step="1"
                  />
                </>
              )}
            </SettingRow>

          </SettingsGrid>

          <SizedBox height={32} />

          {/* Personal, not account-scoped — so unlike Integrations below, this
              shows even while a team member is viewing an owner's account. */}
          <DeviceAlertsSection />

          {accountStore.isViewingOwnAccount && (
            <>
              <SizedBox height={32} />

              <Text
                type="heading"
                size={16}
                color="#494C5B"
                style={{ fontWeight: 700 }}
              >
                {(lang.settings as any).integrationsHeading ?? "Integrations"}
              </Text>
              <SizedBox height={12} />
              <IntegrationsGrid>
                <JohnDeereProfileSection />
                {/* Behind the `cropwise` feature config — only allowlisted users
                    see it, so an integration still being validated against the
                    demo tenant can't surface errors to anyone else. */}
                {settingsStore.hasConfig("cropwise") && <CropwiseProfileSection />}
              </IntegrationsGrid>
            </>
          )}

          {/* ChatGPT/Claude connections with Disconnect. Personal like the
              alerts above: the tokens belong to the logged-in person, so this
              shows in every account view. Hidden while there are none. */}
          <ConnectedAppsGrid>
            <ConnectedAppsSection />
          </ConnectedAppsGrid>
        </Column>

        <SizedBox height={24} />
        <Footer>
          <Button
            onClick={handleCancel}
            kind="outline"
            style={{ padding: "0 20px", maxWidth: "fit-content" }}
          >{lang.settings.cancelButton}</Button>
          <SizedBox width={8} />
          <Button
            disabled={loading}
            onClick={handleSave}
            style={{ padding: "0 16px", width: 210 }}
          >
            {!loading ? `${lang.settings.saveButton}` : <Loading />}
          </Button>
        </Footer>
      </Page>
    </Layout>
  );
};

export default observer(SettingsScreen);
